import { DecideIsFullAmount, useIsFullAmount } from './useIsFullAmount';
import { BigNumber } from '@ethersproject/bignumber';
import {
  Dispatch,
  ReactNode,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLTV } from '../../../hooks/useLTV';
import { useSelectedAsset } from './AssetMenu';
import { useAmountInUSD } from './useAmountInUSD';
import { GetMaxAmount, UseMaxAmount, useMaxTokenAmount } from '../../../../hooks/useMaxTokenAmount';
import { DoSubmit, useSubmission } from './useSubmission';
import { useValidation, ValidateForm } from './useValidation';
import { SiloAsset } from 'queries/decoratedSilo';
import { useThisDecoratedSilo } from '../../silo';
import { useNormalizedTokenInputAmount } from '../../../../hooks/useNormalizedTokenInputAmount';
import { LTV } from 'app/features/util/ltv';
import { SubmissionModalContext } from './SubmissionModal.context';
import { GetNewUtilization, useUtilizationImpact, UtilizationImpact } from '../../../../hooks/useUtilizationImpact';
import { usePriceInUSD } from 'app/queries/price/hooks';
import { ChecksumAddress, ProtocolInstance } from '@silo/types';

export interface UseActionFormProps {
  protocolInstance: ProtocolInstance;
  assetsToDisplay?: SiloAsset[];
  queryStringSelectedAddress?: ChecksumAddress;
  getMaxAmount: GetMaxAmount;
  getNewUtilization: GetNewUtilization;
  doSubmit: DoSubmit;
  validateForm?: ValidateForm;
  decideIsFullAmount?: DecideIsFullAmount;
  onSelectedAddressChange?: (selectedAddress: ChecksumAddress) => void;
  submissionModalContentSlot?: ReactNode;
  disableSubmit?: boolean | ((params: { selectedAsset: SiloAsset }) => boolean);
}

interface UseActionForm extends UseMaxAmount {
  tokenAmount: string;
  tokenAmountRef: RefObject<HTMLInputElement>;
  normalizedTokenAmount: BigNumber | null;
  setTokenAmount: Dispatch<SetStateAction<string>>;
  resetTokenAmount: () => void;
  selectedAddress: ChecksumAddress;
  selectedAsset: SiloAsset;
  setSelectedAddress: (address: ChecksumAddress) => void;
  currentLTV: LTV | null;
  isFullAmount: boolean;
  utilizationImpact: UtilizationImpact;
  isInvalid: boolean;
  canSubmit: boolean;
  setPreventSubmit: Dispatch<SetStateAction<boolean>>;
  errorMessage?: string;
  submitButtonErrorMessage?: string;
  submit: () => void;
  isPending: boolean;
  isSubmissionModalOpen: boolean;
  submissionModalContext: SubmissionModalContext;
}

export const useActionForm = ({
  protocolInstance,
  assetsToDisplay,
  queryStringSelectedAddress,
  getMaxAmount,
  getNewUtilization,
  validateForm,
  doSubmit,
  decideIsFullAmount,
  onSelectedAddressChange,
  submissionModalContentSlot,
  disableSubmit: _disableSubmit,
}: UseActionFormProps): UseActionForm => {
  const decoratedSilo = useThisDecoratedSilo();
  const { assets, siloAddress } = decoratedSilo;

  const [tokenAmount, setTokenAmount] = useState('');
  const [selectedAddress, setSelectedAddress] = useSelectedAddress(
    queryStringSelectedAddress,
    assetsToDisplay,
    assets,
    onSelectedAddressChange
  );

  const tokenAmountRef = useRef<HTMLInputElement>(null);

  const selectedAsset = useSelectedAsset(assets, selectedAddress);
  const normalizedTokenAmount = useNormalizedTokenInputAmount(tokenAmount, selectedAsset.decimals);
  const { amountInUSD, priceInUSD } = useAmountInUSD({ selectedAsset, normalizedAmount: normalizedTokenAmount });
  const bigIntPriceInUSD = usePriceInUSD(selectedAsset.priceInNative.toBigInt());

  const currentLTV = useLTV();
  const _getMaxAmount = useMemo(
    () => () => getMaxAmount({ selectedAsset, currentLTV, decoratedSilo }),
    [selectedAsset, currentLTV, decoratedSilo, getMaxAmount]
  );

  const { maxAmount, maxAmountInUSD, setMaxAmount } = useMaxTokenAmount({
    getMaxAmount: _getMaxAmount,
    setTokenAmount,
    priceInUSD: bigIntPriceInUSD,
  });

  const resetTokenAmount = (): void => {
    setTokenAmount('');
  };

  const isFullAmount = useIsFullAmount({
    decideIsFullAmount,
    normalizedTokenAmount,
    amountInUSD,
    maxAmount,
    maxAmountInUSD,
    selectedAsset,
    priceInUSD,
  });

  const utilizationImpact = useUtilizationImpact({
    siloAddress,
    normalizedTokenAmount,
    selectedAsset,
    getNewUtilization,
  });

  const { isInvalid, errorMessage, ...validationValues } = useValidation({
    protocolInstance,
    decoratedSilo,
    validateForm,
    normalizedTokenAmount,
    maxAmount,
    selectedAsset,
    amountInUSD,
    isFullAmount,
    priceInUSD,
  });

  const { errorMessageFromSubmit, ...submissionValues } = useSubmission({
    doSubmit,
    isFullAmount,
    isInvalid,
    normalizedTokenAmount,
    selectedAsset,
    resetTokenAmount,
    tokenAmountRef,
    isSubmissionModalEnabled: !!submissionModalContentSlot,
  });

  const disableSubmit = typeof _disableSubmit === 'function' ? _disableSubmit({ selectedAsset }) : _disableSubmit;

  return {
    tokenAmount,
    tokenAmountRef,
    normalizedTokenAmount,
    setTokenAmount,
    resetTokenAmount,
    maxAmount,
    maxAmountInUSD,
    setMaxAmount,
    selectedAddress,
    selectedAsset,
    setSelectedAddress,
    currentLTV,
    isFullAmount,
    utilizationImpact,
    isInvalid,
    errorMessage: errorMessage || errorMessageFromSubmit,
    ...validationValues,
    ...submissionValues,
    canSubmit: submissionValues.canSubmit && !disableSubmit,
  };
};

const useSelectedAddress = (
  queryStringSelectedAddress: ChecksumAddress | undefined,
  assetsToDisplay: SiloAsset[] | undefined,
  assets: SiloAsset[],
  onSelectedAddressChange?: (selectedAddress: ChecksumAddress) => void
) => {
  const [selectedAddress, _setSelectedAddress] = useState(
    () => queryStringSelectedAddress ?? assetsToDisplay?.[0]?.assetAddress ?? assets[0]?.assetAddress
  );

  const setSelectedAddress = useCallback(
    (address: ChecksumAddress) => {
      onSelectedAddressChange?.(address);
      _setSelectedAddress(address);
    },
    [onSelectedAddressChange]
  );

  // if query string is changed - that becomes the selected address
  useEffect(() => {
    if (queryStringSelectedAddress && queryStringSelectedAddress !== selectedAddress) {
      setSelectedAddress(queryStringSelectedAddress);
    }
    // disabling the exhaustive deps rule because we only want to run this effect when the query string changes
    // we're only interested in the `selectedAddress` value at that single time.
    // we're not trying to keep `queryStringSelectedAddress` and `selectedAddress` in sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryStringSelectedAddress]);

  /**
   * This safely resets the selected address if the assetsToDisplay no longer contains the selected address.
   *
   * Refs add boilerplate, but are safer than ignoring exhaustive deps.
   */
  const assetsToDisplayRef = useRef(assetsToDisplay);
  assetsToDisplayRef.current = assetsToDisplay;

  const setSelectedAddressRef = useRef(setSelectedAddress);
  setSelectedAddressRef.current = setSelectedAddress;

  const isSelectedAddressInvalid = assetsToDisplay
    ? assetsToDisplay.every((asset) => asset.assetAddress !== selectedAddress)
    : false;

  useEffect(() => {
    const firstAsset = assetsToDisplayRef.current?.[0];

    if (isSelectedAddressInvalid && firstAsset) {
      setSelectedAddressRef.current(firstAsset.assetAddress);
    }
  }, [isSelectedAddressInvalid]);

  return [selectedAddress, setSelectedAddress] as const;
};
